import { requiresMfa } from '@c4ytechnology/authz';

// RBAC Phase 2 MFA hand-off (contract §1, Admin SPA). The orchestrator attests the MFA session;
// admin-api turns the attestation into MFA claims. On 403 MFA_REQUIRED the admin-api client tries
// one silent step-up and retries the request once; if that fails, Layout shows a banner.
// The banner is only for a definitive "no MFA" answer (401/403 from the orchestrator attestation
// or /auth/mfa/step-up — e.g. a session with no recorded MFA time, or admin-api's `stale_mfa`);
// a network error or 5xx is transient and a later request may try again.

export const MFA_REQUIRED_CODE = 'MFA_REQUIRED';

export const MFA_BANNER_TEXT =
  'Critical actions need MFA — sign in again with your email, password and authenticator. Google or BizAuth sign-in cannot unlock critical actions.';

/** OIDC mode (`VITE_ADMIN_AUTH=oidc`): staff SSO is followed by TOTP, so any sign-in method works. */
export const OIDC_MFA_BANNER_TEXT =
  'Critical actions need a recent authenticator check — sign in again and enter your authenticator code.';

/** `/login?mfa=1`: the MFA banner sent the user here; steer them to password + authenticator. */
export const MFA_LOGIN_PARAM = 'mfa';

export const MFA_LOGIN_HINT_TEXT =
  'Critical actions need your authenticator: sign in with your email and password, then enter your authenticator code. Google or BizAuth sign-in cannot unlock critical actions.';

export type MfaRecoveryInput = {
  status: number | undefined;
  /** `code` from the error body. */
  code: unknown;
  /** The request was itself the step-up (or opted out). */
  skipStepUp: boolean;
  /** The request already went through one step-up + retry. */
  alreadyRetried: boolean;
  /** A step-up already failed for the current token; trying again would only loop. */
  stepUpFailedForToken: boolean;
  /**
   * The token the request was sent with differs from the current one (another request's step-up
   * already re-issued it), so the request only needs re-sending with the new token.
   */
  tokenChangedSinceRequest?: boolean;
};

export type MfaRecoveryDecision = 'ignore' | 'step-up' | 'retry' | 'banner';

/** What the admin-api response interceptor does with one failed response. */
export function decideMfaRecovery(input: MfaRecoveryInput): MfaRecoveryDecision {
  if (input.status !== 403 || input.code !== MFA_REQUIRED_CODE) return 'ignore';
  if (input.skipStepUp || input.alreadyRetried) return 'banner';
  if (input.tokenChangedSinceRequest) return 'retry';
  if (input.stepUpFailedForToken) return 'banner';
  return 'step-up';
}

export type StepUpFailureKind = 'definitive' | 'transient';

/**
 * Why a step-up failed. `definitive` (any 401/403 from the attestation or step-up call, or a 2xx
 * that carried no MFA) marks the token so the banner shows; `transient` (no response, 408, 429,
 * 5xx) does not, so a later request can try again.
 */
export function stepUpFailureKind(err: unknown): StepUpFailureKind {
  const e = err as { isAxiosError?: boolean; response?: { status?: unknown } } | null | undefined;
  const status = typeof e?.response?.status === 'number' ? e.response.status : undefined;
  if (status === undefined) return e?.isAxiosError ? 'transient' : 'definitive';
  if (status === 408 || status === 429 || status >= 500) return 'transient';
  return 'definitive';
}

export const MFA_STEP_UP_TRANSIENT_TEXT =
  "Couldn't confirm your authenticator sign-in right now. Try the action again in a moment.";

/** True when the orchestrator session was completed with MFA (attestation can be minted). */
export function orchestratorSessionIsMfa(
  session: { authStrength?: string; mfaVerified?: boolean; authenticated?: boolean } | null | undefined,
): boolean {
  if (!session || session.authenticated === false) return false;
  return session.authStrength === 'mfa' || session.mfaVerified === true;
}

/** True when any held key needs MFA under the catalog. */
export function holdsMfaCriticalKey(permissions: readonly string[] | null | undefined): boolean {
  return (permissions ?? []).some((k) => requiresMfa(k));
}

/**
 * Banner visibility: a failed step-up for the current token, or `/auth/me` saying the token is not
 * MFA-verified while the user holds a critical key. Older admin-api builds without `mfa` show
 * nothing.
 */
export function shouldShowMfaBanner(input: {
  me: { mfa?: { verified: boolean } | null; access?: { permissions: readonly string[] } | null } | null | undefined;
  stepUpFailed: boolean;
}): boolean {
  if (input.stepUpFailed) return true;
  const me = input.me;
  if (!me?.mfa || me.mfa.verified !== false) return false;
  return holdsMfaCriticalKey(me.access?.permissions);
}

// ── Failed step-up state (per token) ─────────────────────────────────────────────────────

export const ADMIN_MFA_STATE_EVENT = 'evolveos:admin-mfa-state';

let failedForToken: string | null = null;

function emit(): void {
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(ADMIN_MFA_STATE_EVENT));
  } catch {
    /* ignore */
  }
}

/** Remember that step-up failed for `token` so the next MFA_REQUIRED goes straight to the banner. */
export function markMfaStepUpFailed(token: string | null): void {
  failedForToken = token ?? '';
  emit();
}

export const ADMIN_MFA_STEPPED_UP_EVENT = 'evolveos:admin-mfa-stepped-up';

/** Tell Layout a silent step-up re-issued the token (so `/auth/me` is refetched). */
export function notifyMfaSteppedUp(): void {
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(ADMIN_MFA_STEPPED_UP_EVENT));
  } catch {
    /* ignore */
  }
}

/** A successful step-up (or a new login) clears the failure. */
export function clearMfaStepUpFailure(): void {
  if (failedForToken === null) return;
  failedForToken = null;
  emit();
}

export function mfaStepUpFailedFor(token: string | null): boolean {
  return failedForToken !== null && failedForToken === (token ?? '');
}
